import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, RotateCcw, ShieldCheck } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { format } from 'date-fns';
import { DISCLAIMER_SECTIONS as SECTIONS } from '@/lib/disclaimerText';

export default function DisclaimerForm({ prefillName, prefillEmail, bookingContext, onComplete }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [name, setName] = useState(prefillName || '');
  const [signedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [guardian, setGuardian] = useState(false);
  const [accessibilityNeeds, setAccessibilityNeeds] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = '#000';
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasSigned) setHasSigned(true);
  };

  const endDraw = () => {
    drawing.current = false;
  };

  const clearSig = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) return setError('Please enter your full name.');
    if (!hasSigned) return setError('Please provide your signature in the box above.');
    if (!emergencyName.trim()) return setError('Please enter an emergency contact name.');
    if (!emergencyPhone.trim()) return setError('Please enter an emergency contact phone number.');

    setSubmitting(true);
    try {
      const blob = await new Promise((resolve) => canvasRef.current.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Signature capture failed');
      const file = new File([blob], 'booking-signature.png', { type: 'image/png' });
      const upload = await base44.integrations.Core.UploadFile({ file });
      if (!upload?.file_url) throw new Error('Upload failed');
      const disclaimerData = {
        customer_name: name.trim(),
        customer_email: prefillEmail || '',
        signature_data_url: upload.file_url,
        signed_date: signedDate,
        emergency_contact_name: emergencyName.trim(),
        emergency_contact_phone: emergencyPhone.trim(),
        accessibility_needs: accessibilityNeeds.trim(),
        signed_as_guardian: guardian,
        booking_ids: bookingContext?.bookingIds?.join(',') || '',
        experience_type: bookingContext?.experienceType || '',
        session_date: bookingContext?.sessionDate || '',
        payment_id: bookingContext?.paymentId || '',
      };
      await base44.entities.Disclaimer.create(disclaimerData);
      // Fire the branded PDF copy to the customer + admin inbox (best-effort,
      // non-blocking — the record is already saved so this must not gate the flow).
      base44.functions
        .invoke('sendDisclaimerCopy', { data: disclaimerData })
        .catch((e) => console.error('Disclaimer copy email failed:', e?.message || e));
      onComplete && onComplete();
    } catch (err) {
      setError('Could not save your disclaimer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase = 'w-full bg-[#FEFEE0] border-b border-black px-3 py-2 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:border-primary';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto bg-white text-black shadow-2xl"
    >
      {/* Black header */}
      <div className="bg-black px-6 py-5 flex items-center gap-3">
        <div className="w-9 h-9 bg-[#E30613] flex items-center justify-center font-black text-white text-lg italic">M</div>
        <div className="leading-none">
          <p className="text-white font-black text-base tracking-tight">MOTION</p>
          <p className="text-white/80 text-[9px] tracking-[0.3em] leading-tight">REALITY</p>
          <p className="text-white/60 text-[8px] tracking-[0.3em] leading-tight">SIM RACING</p>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 sm:px-10 py-8">
        <h1 className="text-center font-black text-xl sm:text-2xl mb-1">Motion Reality Sim Rig User Disclaimer (UK)</h1>
        <p className="text-center font-bold text-sm mb-4">Important Notice</p>
        <p className="text-sm leading-relaxed mb-6">By using this sim racing rig, you confirm that you have read and understood the following terms:</p>

        <div className="space-y-4 text-sm leading-relaxed">
          {SECTIONS.map((s, i) => (
            <div key={i}>
              <p className="font-bold mb-1">{s.title}</p>
              {s.body && <p>{s.body}</p>}
              {s.bullets && (
                <ul className="list-disc pl-5 space-y-1 mt-1">
                  {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <div>
            <label className="block text-xs font-bold mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border-b border-black bg-transparent px-1 py-1.5 text-sm focus:outline-none focus:border-[#E30613]" />
          </div>

          <div>
            <label className="block text-xs font-bold mb-1">
              Signature <span className="text-[#FF0000]">*</span> <span className="text-gray-500 font-normal">(Parent or Guardian if under 16)</span>
            </label>
            <div className="relative">
              <canvas
                ref={canvasRef}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
                className="w-full h-24 bg-[#FEFEE0] border border-black touch-none cursor-crosshair"
              />
              {!hasSigned && (
                <span className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm pointer-events-none">Click here to sign</span>
              )}
              <button type="button" onClick={clearSig} className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 text-[10px] text-black/60 hover:text-black bg-white/80 px-2 py-1">
                <RotateCcw className="w-3 h-3" /> Clear
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold mb-1">Date</label>
              <input value={format(new Date(signedDate), 'dd MMM yyyy')} readOnly className={`${inputBase} cursor-default`} />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Emergency Contact name <span className="text-[#FF0000]">*</span></label>
              <input value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} className={inputBase} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold mb-1">Emergency Contact phone number <span className="text-[#FF0000]">*</span></label>
            <input value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} className={inputBase} />
          </div>

          <div>
            <label className="block text-xs font-bold mb-1">Accessibility & Support <span className="text-gray-500 font-normal">(optional)</span></label>
            <textarea
              value={accessibilityNeeds}
              onChange={(e) => setAccessibilityNeeds(e.target.value)}
              rows={3}
              placeholder="Please let us know if anyone in your group has mobility needs, access requirements, or requires assistance getting in and out of the rigs so we can best support you."
              className="w-full border border-black bg-[#FEFEE0] px-3 py-2 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:border-[#E30613] resize-none"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-black/70">
            <input type="checkbox" checked={guardian} onChange={(e) => setGuardian(e.target.checked)} className="accent-[#E30613]" />
            I am signing as a parent or guardian on behalf of a user under 16
          </label>

          {error && <p className="text-[#FF0000] text-xs font-medium">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-black text-white font-bold text-sm py-3.5 flex items-center justify-center gap-2 hover:bg-[#E30613] transition-colors disabled:opacity-50"
          >
            {submitting ? (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <><ShieldCheck className="w-4 h-4" /> I AGREE — SUBMIT DISCLAIMER</>
            )}
          </button>
        </form>
      </div>

      {/* Black footer */}
      <div className="bg-black px-6 py-4 text-center text-white/90 text-[11px] space-y-0.5">
        <p>Tel: 0151 528 3001</p>
        <p>sales@motionreality.co.uk</p>
        <p>Unit 9, Croft Business Park, Carrock Road, Bromborough, CH62 3RA</p>
      </div>
    </motion.div>
  );
}